import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface UserDto {
  id: number;
  entraUserId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ManagedUserListItemDto {
  userId: number;
  entraUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  organizationId: number;
  organizationName: string;
  role: 'SiteAdmin' | 'OrgAdmin' | 'EventPlanner' | 'TeamMember' | string;
  smsNotificationsEnabled?: boolean;
  mediaUploadConsentEnabled?: boolean;
}

export interface ManagedUserMembershipDto {
  organizationId: number;
  organizationName: string;
  role: 'SiteAdmin' | 'OrgAdmin' | 'EventPlanner' | 'TeamMember' | string;
  smsNotificationsEnabled?: boolean;
  mediaUploadConsentEnabled?: boolean;
}

export interface ManagedUserMediaDto {
  artifactId: number;
  organizationId: number;
  artifactType: string;
  artifactCategory: string;
  artifactScope: string;
  fileName: string;
  contentType: string;
  blobPath: string;
  sizeBytes: number;
  isEnabled: boolean;
  default?: boolean | null;
  dateCreated: string;
}

export interface ManagedUserDetailDto {
  userId: number;
  entraUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  memberships: ManagedUserMembershipDto[];
  media: ManagedUserMediaDto[];
}

export interface OrganizationUserDto {
  id: number;
  organizationId: number;
  userId: number;
  roleId: number;
  user?: UserDto;
  role?: RoleDto;
}

export interface RoleDto {
  id: number;
  name: 'SiteAdmin' | 'OrgAdmin' | 'EventPlanner' | 'TeamMember';
  description: string;
}

export interface UserCreateDto {
  entraUserId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UserUpdateDto {
  firstName: string;
  lastName: string;
  email: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private readonly http = inject(HttpClient);
  private readonly usersApiUrl = '/api/users';
  private readonly organizationUsersApiUrl = '/api/organizations';

  /**
   * Get all users (SiteAdmin only)
   */
  getAll(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.usersApiUrl);
  }

  /**
   * Get managed users scoped by role/organization.
   */
  getManagedUsers(): Observable<ManagedUserListItemDto[]> {
    return this.http.get<ManagedUserListItemDto[]>(`${this.usersApiUrl}/management`);
  }

  getManagedUsersByOrganization(organizationId: number): Observable<ManagedUserListItemDto[]> {
    return this.http.get<ManagedUserListItemDto[]>(`${this.usersApiUrl}/management/organization/${organizationId}`);
  }

  /**
   * Get managed user details including org memberships and media.
   */
  getManagedUserDetail(userId: number): Observable<ManagedUserDetailDto> {
    return this.http.get<ManagedUserDetailDto>(`${this.usersApiUrl}/management/${userId}`);
  }

  getUserMediaContent(artifactId: number): Observable<Blob> {
    return this.http.get(`/api/artifactassets/${artifactId}/content`, { responseType: 'blob' });
  }

  /**
   * Get user by ID
   */
  getById(id: number): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.usersApiUrl}/${id}`);
  }

  /**
   * Create new user (SiteAdmin only)
   */
  create(data: UserCreateDto): Observable<UserDto> {
    return this.http.post<UserDto>(this.usersApiUrl, data);
  }

  /**
   * Update user
   */
  update(id: number, data: UserUpdateDto): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.usersApiUrl}/${id}`, data);
  }

  /**
   * Delete user (SiteAdmin only)
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.usersApiUrl}/${id}`);
  }

  /**
   * Get organization users with roles
   */
  getOrganizationUsers(organizationId: number): Observable<OrganizationUserDto[]> {
    return this.http.get<OrganizationUserDto[]>(
      `${this.organizationUsersApiUrl}/${organizationId}/users`
    );
  }

  /**
   * Get all roles
   */
  getRoles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(`/api/roles`);
  }

  /**
   * Assign role to user in organization
   * Note: This may need to be implemented as a separate endpoint
   */
  assignRoleToUser(
    organizationId: number,
    userId: number,
    roleId: number
  ): Observable<OrganizationUserDto> {
    return this.http.post<OrganizationUserDto>(
      `${this.organizationUsersApiUrl}/${organizationId}/users/${userId}/role`,
      { roleId }
    );
  }

  /**
   * Remove user from organization
   */
  removeUserFromOrganization(
    organizationId: number,
    userId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.organizationUsersApiUrl}/${organizationId}/users/${userId}`
    );
  }
}
